import React, { useState, useEffect } from 'react';
import { downloadVariantPdf } from '../../utils/variantPdf';
import toast from 'react-hot-toast';
import Loader from '../../components/Common/Loader';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyCourse, getSections, getProgress, getHomeworks, getVariants, getInviteCode } from '../../services/studentService';
import { computeCourseProgress } from '../../utils/progressStats';

const CURATOR_TG = 'informatic_artem';

/* Копирование с запасным способом: navigator.clipboard есть только на
   защищённом соединении, а на телефон платформу открывают и по локальному
   адресу по http — там он недоступен. */
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch (e) { /* запрещено — пробуем по-старому */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
}

const MonthRu = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];
const DaysRu = ['Воскресенье','Понедельник','Вторник','Среда','Четверг','Пятница','Суббота'];

/* ── icons ── */
const IcClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IcEdit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IcClip = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/>
    <line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/>
  </svg>
);
const IcFire = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
  </svg>
);
const IcDownload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const IcGift = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5" rx="1"/>
    <line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>
);
const IcShare = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
  </svg>
);
const IcTg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 3 2 10.5l6 2.5 2.5 6L14 15l6 6z"/><path d="m8 13 6-4"/>
  </svg>
);
const IcDemo = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);

/* кольцо прогресса */
function Ring({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: 92, height: 92, flexShrink: 0 }}>
      <svg viewBox="0 0 92 92" width="92" height="92">
        <circle cx="46" cy="46" r={r} fill="none" stroke="#ece8f4" strokeWidth="9" />
        <circle cx="46" cy="46" r={r} fill="none" stroke="url(#ringgrad)" strokeWidth="9" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform="rotate(-90 46 46)" />
        <defs>
          <linearGradient id="ringgrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" /><stop offset="100%" stopColor="#6d28d9" />
          </linearGradient>
        </defs>
      </svg>
      <div className="prog-ring-num" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {pct}<span className="pc">%</span>
      </div>
    </div>
  );
}

// Дата занятия в карточке «Активные задания» — как у админа в «Уроках курса»: ДД.ММ
const shortDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  if (isNaN(x)) return '—';
  return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}`;
};

export default function HomePage() {
  const [progress, setProgress] = useState(null);
  const [homeworks, setHomeworks] = useState([]);
  const [variants, setVariants] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedCourseId, course } = useOutletContext() || {};

  useEffect(() => {
    Promise.all([
      getMyCourse(selectedCourseId).catch(() => {}),
      getProgress(selectedCourseId).then(r => setProgress(r.data)).catch(() => {}),
      getHomeworks(selectedCourseId).then(r => setHomeworks(r.data || [])).catch(() => {}),
      getVariants(selectedCourseId).then(r => setVariants(r.data || [])).catch(() => {}),
      getSections(selectedCourseId).then(r => setSections(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [selectedCourseId]);

  // Старт урока в МСК (date + time). PostgreSQL TIME → "HH:MM:SS", берём HH:MM
  const lessonStart = (l) => {
    if (!l.date || !l.time) return null;
    const d = new Date(`${l.date}T${l.time.slice(0, 5)}:00+03:00`);
    return isNaN(d) ? null : d;
  };
  const now = new Date();
  const scheduled = sections
    .flatMap(s => (s.Lessons || []).map(l => ({ ...l, sectionName: s.name })))
    .filter(l => !l.isHomeworkOnly)
    .map(l => {
      const start = lessonStart(l);
      const durMs = (l.duration > 0 ? l.duration : 120) * 60 * 1000;
      return { ...l, _start: start, _end: start ? new Date(start.getTime() + durMs) : null };
    })
    .filter(l => l._start)
    .sort((a, b) => a._start - b._start);

  // «Идёт сейчас» — от начала и до конца урока по длительности.
  // Если идущего нет — берём ближайший будущий. Так текущий урок не пропадает
  // в момент, когда время старта прошло (раньше сразу показывался следующий).
  const liveLesson = scheduled.find(l => now >= l._start && now <= l._end);
  const upcomingLesson = scheduled.find(l => now < l._start);
  const nextLesson = liveLesson || upcomingLesson || null;
  const isLive = !!nextLesson && nextLesson === liveLesson;
  const liveMin = isLive ? Math.max(0, Math.floor((now - nextLesson._start) / 60000)) : 0;
  const liveDurStr = (() => { const h = Math.floor(liveMin / 60), m = liveMin % 60; return h > 0 ? `${h} ч ${m} мин` : `${m} мин`; })();

  // ДЗ группируем по урокам: одно ДЗ = урок. Активно = урок ещё не завершён
  // (ученик не нажал «Завершить»). Показываем целые ДЗ, а не отдельные номера.
  const hwGroups = (() => {
    const map = {};
    const groups = [];
    homeworks.forEach(hw => {
      const lid = hw.lessonId || hw.Lesson?.id;
      if (lid == null) return;
      if (!map[lid]) { map[lid] = { lessonId: lid, title: hw.Lesson?.title || 'Урок', date: hw.Lesson?.date || null, completed: false, answered: false }; groups.push(map[lid]); }
      if (hw.answer) map[lid].answered = true;          // начато
      if (hw.lessonCompleted) map[lid].completed = true;
    });
    // «Начато» также при локальном черновике (набрано, но не сдано).
    groups.forEach(g => {
      if (g.completed || g.answered) return;
      try {
        const dr = JSON.parse(localStorage.getItem(`hwdraft:${g.lessonId}`) || '{}');
        if (Object.values(dr).some(v => v && String(v).trim() && String(v).trim() !== '[]')) g.answered = true;
      } catch (e) {}
    });
    return groups;
  })();
  const activeHw = hwGroups.filter(g => !g.completed);
  const activeVariants = variants.filter(v => !v.completed);
  // ДЗ первыми, затем варианты; максимум 3 в карточке — так она не растёт вниз
  // и карточкам под ней остаётся больше места
  const activeItems = [
    ...activeHw.map(g => ({ kind: 'hw', id: g.lessonId, title: g.title, date: g.date, answered: g.answered })),
    ...activeVariants.map(v => ({ kind: 'variant', id: v.id, title: v.title, date: v.date, answered: v.answered })),
  ].slice(0, 3);
  const hasAny = homeworks.length > 0 || variants.length > 0;
  // «Все →»: если есть активные ДЗ — ведём на «Активные» в ДЗ; иначе (только варианты) — на «Варианты»
  const seeAllActive = () => activeHw.length > 0
    ? navigate('/homework', { state: { tab: 'active' } })
    : navigate('/tests');

  // «Общий прогресс» — тот же расчёт, что в разделе Прогресс (доля пройденного от всех занятий).
  const cstats = computeCourseProgress(progress, homeworks, variants);
  const pct = cstats.pct;
  const firstName = user?.name ? user.name.split(' ')[0] : 'Ученик';

  // «Что было на ЕГЭ/ОГЭ» — по плашке курса: у ОГЭ по информатике 16 заданий, у ЕГЭ 27.
  const isOge = course?.examType === 'ОГЭ';
  const exam = isOge ? 'ОГЭ' : 'ЕГЭ';
  const examTasks = isOge ? 16 : 27;
  // Вариант для карточки — последний вариант курса со словами «Основная волна» в
  // названии: id в локальной базе и на проде разные, а название одно. Нет или ещё
  // не опубликован — сообщаем, что скоро появится.
  const examVariant = [...variants].reverse()
    .find(v => String(v.title || '').toLowerCase().includes('основная волна'));
  const openExamVariant = () => (examVariant
    ? navigate(`/variant/${examVariant.id}`, { state: { from: '/home' } })
    : toast(`Вариант «Что было на ${exam}» скоро появится`, { icon: '📝' }));
  /* Реферальная программа. Второй код заводить не стали — ученик и так узнаётся
     по имени, а имя уезжает в готовом тексте приглашения: друг просто пересылает
     его куратору, ничего не запоминая и не вводя. */
  /* Ссылка на страницу приглашения. В адресе короткий код, а не имя: кириллица
     превращалась в %D0%9A%D1%83… и ссылка выглядела подозрительно. Имя страница
     узнаёт по коду сама. */
  const [inviteCode, setInviteCode] = useState('');
  useEffect(() => { getInviteCode().then(r => setInviteCode(r?.data?.code || '')).catch(() => {}); }, []);
  const inviteLink = `${window.location.origin}/r/${inviteCode}`;
  const inviteText = [
    `Готовлюсь к ${exam} по информатике в ИНФА СОТКЕ — рекомендую.`,
    'Если придёшь по моей рекомендации, тебе скидка 500 ₽ на первый месяц.',
    '',
    inviteLink,
  ].join('\n');

  const invite = async () => {
    // На телефоне отдаём системное «Поделиться» — оттуда сразу в чат с другом.
    // На компьютере оно открывает неудобную панель, поэтому просто копируем.
    const touch = window.matchMedia?.('(pointer: coarse)')?.matches;
    if (touch && navigator.share) {
      try { await navigator.share({ text: inviteText }); return; } catch (e) { if (e?.name === 'AbortError') return; }
    }
    if (await copyText(inviteText)) toast.success('Приглашение скопировано — отправь другу');
    else toast.error('Не удалось скопировать');
  };

  const openExamPdf = () => (examVariant
    ? downloadVariantPdf(examVariant.id, examVariant.title)
    : toast(`Вариант «Что было на ${exam}» скоро появится`, { icon: '📝' }));

  const nd = nextLesson?.date ? new Date(nextLesson.date) : null;

  if (loading) return <div className="s-page"><Loader /></div>;

  return (
    <div className="s-page">
      {/* Приветствие */}
      <div className="greet">
        <h1 className="s-page-title greet-h">Привет, {firstName}!</h1>
        <p className="greet-p">Рад видеть тебя на платформе. Продолжай учиться и достигай новых целей!</p>
      </div>

      {/* Ряд 1: ближайший урок + прогресс */}
      <div className="grid-2">
        <div className="panel">
          <div className="panel-cap">Ближайший урок</div>
          {nextLesson ? (
            <div className="next-row">
              <div className="dbadge today">
                <span className="d">{nd.getDate()}</span>
                <span className="m">{MonthRu[nd.getMonth()]}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="next-title">{nextLesson.title}</div>
                <div className="next-meta">
                  {isLive
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#16a34a', fontWeight: 700 }}><span className="pg-live-dot" />Идёт сейчас</span>
                    : <><IcClock /><span>{DaysRu[nd.getDay()]}{nextLesson.time ? `, ${nextLesson.time.slice(0, 5)}` : ''}</span></>}
                  {nextLesson.sectionName && <><span className="sep">·</span><span>{nextLesson.sectionName}</span></>}
                </div>
                <div className="next-actions">
                  {isLive ? (
                    <>
                      <button className="pg-btn green" onClick={() => navigate(`/lesson/${nextLesson.id}`, { state: { from: '/home' } })}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', boxShadow: '0 0 0 2px rgba(255,255,255,0.4)', display: 'inline-block', flexShrink: 0, animation: 'pulse 1.5s infinite' }} />
                        Войти в урок
                      </button>
                      <span style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 500, whiteSpace: 'nowrap' }}>идёт {liveDurStr}</span>
                    </>
                  ) : (
                    <button className="pg-btn" onClick={() => navigate(`/lesson/${nextLesson.id}`, { state: { from: '/home' } })}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff', display: 'inline-block', flexShrink: 0 }} />
                      Перейти к уроку
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text-3)', fontSize: 14, padding: '10px 0' }}>Предстоящих уроков нет</div>
          )}
        </div>

        <div className="panel prog-panel">
          <div className="panel-cap">Общий прогресс</div>
          <div className="prog-row">
            <Ring pct={pct} />
            <div>
              <div className="prog-big">Курс пройден на {pct}%</div>
              <div className="prog-sub">Вебинары {cstats.web.done}/{cstats.web.total} · ДЗ {cstats.hw.done}/{cstats.hw.total} · Варианты {cstats.variant.done}/{cstats.variant.total}</div>
              <button className="pg-link-btn" onClick={() => navigate('/progress')}>Смотреть детали →</button>
            </div>
          </div>
        </div>
      </div>

      {/* Ряд 2: активные задания + активность (демо) */}
      <div className="grid-2 mt">
        <div className="panel">
          <div className="panel-head">
            <div className="panel-cap nomb">Активные задания</div>
            <button className="pg-link-btn" onClick={seeAllActive}>Все →</button>
          </div>
          {!hasAny ? (
            <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0', fontSize: 13 }}>
              Заданий на курсе пока нет
            </div>
          ) : activeItems.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0', fontSize: 13 }}>
              Все задания решены — молодец!
            </div>
          ) : (
            <div className="mini-list">
              {activeItems.map(it => (
                <div key={`${it.kind}-${it.id}`} className="mini-item" onClick={() => navigate(`/homework/${it.id}`, { state: { from: '/home' } })}>
                  <div className="mini-date">{shortDate(it.date)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mini-title">{it.title}</div>
                    <div className="mini-sub">{it.kind === 'variant' ? 'Вариант' : 'Домашнее задание'}</div>
                  </div>
                  <span className={`pg-chip ${it.answered ? 'blue' : 'violet'}`}>{it.answered ? 'Продолжить' : 'Выполнить'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Активность — демо-блок (нет в бэкенде) */}
        <div className="panel streak-panel">
          <div className="panel-cap">Активность<span className="demo-tag"><IcDemo />демо</span></div>
          <div className="streak-row">
            <div className="streak-fire"><IcFire /></div>
            <div>
              <div className="streak-big">12 дней подряд</div>
              <div className="prog-sub">Ты занимаешься без пропусков почти две недели.</div>
            </div>
          </div>
          <div className="week">
            {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map((d, i) => (
              <div key={i} className="week-day">
                <span className={'week-dot' + (i < 5 ? ' on' : '') + (i === 3 ? ' today' : '')} />
                <span className="week-l">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ряд 3: итоги экзамена и реферальная программа — две карточки в ряд */}
      <div className="grid-2 mt home-duo">
      <div className="exam-card" role="button" tabIndex={0} onClick={openExamVariant}
        onKeyDown={(e) => { if (e.key === 'Enter') openExamVariant(); }}>
        <div className="exam-glow" />
        <div className="exam-row">
          <div className="exam-text">
            <div className="exam-cap">Итоги экзамена</div>
            <h2 className="exam-title">Что было на {exam}? <span>2026</span></h2>
            <div className="exam-meta">
              <IcClip />
              <span>Основная волна · {examTasks} заданий</span><span className="sep">·</span>
              <span>После сдачи откроется текстовое решение под каждым номером</span>
            </div>
          </div>
          <div className="exam-acts">
            <button type="button" className="exam-go" onClick={(e) => { e.stopPropagation(); openExamVariant(); }}>
              <i />Решать вариант
            </button>
            <button type="button" className="exam-pdf" onClick={(e) => { e.stopPropagation(); openExamPdf(); }}>
              <IcDownload />Скачать PDF
            </button>
          </div>
        </div>
      </div>

      {/* Реферальная программа — тот же язык, что у карточки экзамена */}
      <div className="exam-card ref-card">
        <div className="exam-glow ref-glow" />
        <div className="exam-row">
          <div className="exam-text">
            <div className="exam-cap"><span className="ref-cap-ic"><IcGift /></span>Реферальная программа</div>
            <h2 className="exam-title">Позови друга — <span>+500 ₽</span></h2>
            <div className="exam-meta">
              <IcGift />
              <span>Другу — скидка 500 ₽ на первый месяц</span><span className="sep">·</span>
              <span>Тебе — 500 ₽, когда он оплатит обучение</span>
            </div>
            <div className="ref-steps">
              <span><i>1</i>Отправляешь другу готовое приглашение</span>
              <span><i>2</i>Он пишет куратору и говорит, что от тебя</span>
              <span><i>3</i>После его оплаты бонус твой</span>
            </div>
          </div>
          <div className="exam-acts">
            <button type="button" className="exam-go" onClick={invite}>
              <IcShare />Пригласить друга
            </button>
            <a className="exam-pdf" href={`https://t.me/${CURATOR_TG}`} target="_blank" rel="noopener noreferrer">
              <IcTg />Спросить куратора
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
